/** The Tags grid search namespaces wired to their suggestion fetchers.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators` on
 * TagFilterNode (contract-tested). `exact` is deliberately omitted wherever
 * `iexact` alone would serialise to `=` and re-parse to `iexact` (name is
 * icontains+iexact only). `category` and `user` keep BOTH `exact` and `iexact`
 * so the widget offers the "by ID" (`=#`) affordance (operators.ts needs both).
 * `vehicle` and `id` are exact-only integer (round-trip fine).
 *
 * Every relation on Group_Tour_Tag is a forward, single-valued FK, so the
 * backend compiles these to plain lookups (no Exists()). `tenant` is NOT a
 * namespace — it stays the toolbar tenant picker (`tour_operator_id`), which
 * also scopes the category/user suggestions via extraFilterValues.
 */
import type { NamespaceConfigMap, SuggestionFetcher } from '@/components/QuerySearch';

import * as categoriesApi from '@/api/tagCategories';
import * as tagsApi from '@/api/tags';
import * as usersApi from '@/api/users';

const YES_NO = ['yes', 'no'];

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

/** Suggest tags by name (id kept for a future `=#` pin, harmless if unused).
 *  Scoped to the picked tenant when one is selected. */
const fetchTagNames: SuggestionFetcher = async (query, extra) => {
  if (!query.trim()) return [];
  const res = await tagsApi.list({
    search: query,
    pageSize: 10,
    ...(extra.tour_operator_id ? { tour_operator_id: extra.tour_operator_id } : {}),
  });
  return res.results.map((t) => ({ value: t.name, label: t.name, id: t.id }));
};

/** Tenant-scoped category suggestions — `extra.tour_operator_id` comes from the
 *  page's `extraFilterValues` when a tenant is picked. The `id` drives `=#`. */
const fetchCategories: SuggestionFetcher = async (query, extra) => {
  if (!query.trim()) return [];
  const res = await categoriesApi.list({
    search: query,
    pageSize: 10,
    ...(extra.tour_operator_id ? { tour_operator_id: extra.tour_operator_id } : {}),
  });
  return res.results.map((c) => ({ value: c.name, label: `${c.name} (${c.code})`, id: c.id }));
};

/** User suggestions by email/name — the `id` drives `=#` (closes the latent
 *  backend id-only filter). */
const fetchUsers: SuggestionFetcher = async (query) => {
  if (!query.trim()) return [];
  const res = await usersApi.list({ search: query, pageSize: 10 });
  return res.results.map((u) => {
    const full = [u.first_name, u.last_name].filter(Boolean).join(' ');
    return { value: u.email || String(u.id), label: full || u.email || `#${u.id}`, id: u.id };
  });
};

export function buildTagNamespaces(): NamespaceConfigMap {
  return {
    name: {
      label: 'name',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      fetchSuggestions: fetchTagNames,
      help: 'Tag name.',
      examples: ['name=~balance', 'name="Checked in"'],
    },
    custom_id: {
      label: 'custom_id',
      operators: ['iexact', 'icontains'],
      valueType: 'string',
      help: "External ID \u2014 the source system's id for the entity an externally identified tag mirrors.",
      examples: ['custom_id=a7a5fb23', 'custom_id=~a7a5'],
    },
    category: {
      label: 'category',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: fetchCategories,
      help: 'Category by name or code; pick a suggestion to pin by id. Scoped to the picked tenant.',
      examples: ['category=checkin', 'category=~note'],
    },
    user: {
      label: 'user',
      operators: ['iexact', 'icontains', 'exact'],
      valueType: 'string',
      fetchSuggestions: fetchUsers,
      help: 'Associated user by email/name; pick a suggestion to pin by id.',
      examples: ['user=~@acme.com', 'user=jane@acme.com'],
    },
    vehicle: {
      label: 'vehicle',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Associated vehicle id.',
      examples: ['vehicle=42'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When created, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['created=2026-01-01..2026-07-01'],
    },
    updated: {
      label: 'updated',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When last updated, UTC.',
      examples: ['updated=2026-07-01..2026-08-01'],
    },
    is_deleted: {
      label: 'is_deleted',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(YES_NO),
      help: 'Surface soft-deleted tags (hidden by default). yes/no.',
      examples: ['is_deleted=yes'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'integer',
      help: 'Tag id.',
      examples: ['id=1234'],
    },
  };
}
